<script setup lang="ts">
import type { GameDepositSubmitPayload } from './StepAmount.vue';
import { isBankAccountDepositMethod } from './bank-account-consent';
import type { GameDepositApprovedInfo } from '~/composables/useGameDepositModal';
import { useUserJourney } from '~/composables/apostatudo/use-user-journey';
import { useModalBannersStore } from '~/stores/apostatudo/modal-banners';
import { useResponsiveBannerUrl } from '~/composables/apostatudo/use-responsive-banner-url';
import { useDepositBannerType } from '~/composables/apostatudo/use-deposit-banner-type';

// Modal de depósito da tela de jogo (ENG-361). Casca slide-up (abre de baixo
// pra cima em mobile e desktop), independente do modal de depósito global.
// Reaproveita os componentes internos do fluxo principal e replica a lógica
// de POST /wallet/add-credit + polling de confirmação, mas com estado próprio.

const SEVEN_SECONDS_IN_MS = 7 * 1000;

const { $metrics, $containment } = useNuxtApp();
const { isMobile } = useLayout();
const { t } = useTexts();

const {
  isOpen,
  step,
  paymentRes,
  isDenied,
  approvedInfo,
  close,
  backToForm,
  goToStep,
} = useGameDepositModal();

const { ApiService } = useApiService();
const { parameters, utmSource, utmMedium, utmCampaign, gaClientId } =
  useParameters();

const {
  userCurrency,
  userCountry,
  userId,
  userHasDocument,
  fetchUserProfile,
  hasFirstDeposit,
  hasBankAccount,
  userBankAccounts,
} = useUserData();

const { showMoneyFormat, updateWallet, walletHasFirstDeposit } = useWallet();
const { updateUserDocumentCPF, updateUserDocumentCURP } = useUserDataStore();
const { openToaster, openChallenge } = useLayoutStore();
const { makeDepositEvent } = useUserJourney();
const { autoSubmitDeposit, autoSubmitDepositAmount, currentDepositMethod } =
  storeToRefs(useWalletStore());

const { clearAutoSubmitDeposit } = useWalletStore();

// Valor inicial da Etapa 1: respeita deeplink/openPixDeposit, senão o padrão
// do card (R$ 250). Copiado pra um ref local no watch(isOpen) ANTES do
// clearAutoSubmitDeposit(): o watch roda pre-flush, antes do StepAmount
// montar, então ler direto do store aqui devolvia '' e o form nascia em 250.
const deeplinkAmount = ref('');

const initialAmount = computed(() => deeplinkAmount.value || '250');

const minDepositAmount = computed(
  () => parameters.value?.min_deposit_value || 100,
);

const maxDepositAmount = computed(
  () => parameters.value?.max_deposit_value || 4900000,
);

const isLoading = ref(false);
const isCountdownDone = ref(false);

// Erro de limite (`limit_error` do core): vai INLINE no formulário, não no
// toaster — a mensagem vem com HTML (link pros limites) e o jogador precisa
// dela parada na tela, não sumindo em 10s. Mesmo tratamento do fluxo antigo.
const limitAlertError = ref<string>('');

// O ref vive neste componente sempre montado; sem isso o erro da última
// tentativa reaparecia já na abertura seguinte do modal.
watch(isOpen, () => {
  limitAlertError.value = '';
});

// Mensagem de primeiro depósito (backoffice, via /parameters): só aparece com
// o bônus de primeiro depósito ativo e enquanto o jogador não depositou.
const firstDepositMessage = computed(() => {
  const message = parameters.value?.deposit_message;

  if (!message || message === 'null') return undefined;

  return message;
});

const activeFirstDepositMessage = computed<boolean>(
  () =>
    !!parameters.value?.first_deposit_bonus &&
    !!parameters.value?.first_deposit_bonus_active &&
    !walletHasFirstDeposit.value &&
    !!firstDepositMessage.value,
);

// Medida Provisória: com a suspensão ligada o modal só mostra o aviso — sem
// banner, sem etapas e sem auto-submit (utils/deposit-suspension.ts).
const isSuspended = isDepositSuspended();

// Título por plataforma (Figma): desktop "Depósito em 1min", mobile "Faça seu
// depósito" — constante nas 3 etapas.
const title = computed(() => {
  if (isSuspended) return 'Depositar';

  return isMobile.value ? 'Faça seu depósito' : 'Depósito em 1min';
});

// Banner da Etapa 1 (desktop e mobile). Servido exclusivamente pelo backoffice, numa slug
// por FTD (ENG-838): /banner-deposito-sem-ftd ou /banner-deposito (com FTD) (mesmo
// adapter dos banners do perfil oficial). Sem fallback: se o backoffice não
// retornar imagem, o banner não é exibido.
const modalBannersStore = useModalBannersStore();
const { getResponsiveBannerUrl } = useResponsiveBannerUrl();
const { depositBannerType } = useDepositBannerType();

// Busca o banner do backoffice ao abrir o modal (store cacheia por 5min). Observa
// o tipo também: ele é `null` até a carteira chegar, e troca para "com FTD" depois
// do primeiro depósito.
watch(
  [isOpen, depositBannerType],
  ([open, type]) => {
    if (open && type && !isSuspended) {
      modalBannersStore.fetchBanner(type, isMobile.value);
    }
  },
  { immediate: true },
);

const bannerImage = computed(() => {
  if (!depositBannerType.value) return null;

  const apiUrl = modalBannersStore.getImageUrl(
    depositBannerType.value,
    isMobile.value,
  );

  return apiUrl ? getResponsiveBannerUrl(apiUrl) : null;
});

const bannerLink = computed(() => {
  if (!depositBannerType.value || !bannerImage.value) return null;

  return modalBannersStore.getLinkUrl(depositBannerType.value, isMobile.value);
});

// Link externo abre em nova aba; rota do site navega na mesma.
const isExternalBannerLink = computed(() =>
  /^https?:\/\//.test(bannerLink.value ?? ''),
);

// Minha Conta V2 é um painel de estado PRÓPRIO (useUserV2Modal): fechar o
// depósito não o alcança, e o depósito pode ter sido aberto de dentro dele.
const { close: closeUserV2Modal, getScreenForPath } = useUserV2Modal();

// Rota do site: o modal é montado uma vez no layout e não fecha sozinho na troca
// de rota, então sem isto a página mudaria escondida atrás dele. Link externo abre
// em nova aba e o modal fica — o depósito continua ali quando o usuário voltar.
function onBannerClick() {
  if (isExternalBannerLink.value) return;

  close();

  // Mesma regra do user-v2/Shortcuts.vue e do withdraw/Modal/StepSuccess.vue:
  // rota com tela no painel é interceptada pelo middleware userV2Modal e vira
  // troca de tela; qualquer outra passa direto e deixaria o painel por cima.
  if (!getScreenForPath(bannerLink.value ?? '')) closeUserV2Modal();
}

// --- Submissão ---------------------------------------------------------------

async function onSubmit(payload: GameDepositSubmitPayload) {
  isLoading.value = true;

  try {
    if (!userHasDocument.value) {
      const registered = await submitDocument(payload);

      if (!registered) return;
    }
    await runValidationThenDeposit(payload.amount, payload.saveBankAccount);
  } finally {
    isLoading.value = false;
  }
}

// Cadastra o CPF/CURP antes do depósito. Devolve false se não deu (o toaster
// já foi mostrado): sem isso a rejeição do core virava unhandled rejection e o
// jogador via o spinner sumir sem nenhuma mensagem.
async function submitDocument(
  payload: GameDepositSubmitPayload,
): Promise<boolean> {
  if (!payload.doc.docnumber) {
    openToaster({ message: t('deposit.errors.insert_cpf'), type: 'error' });

    return false;
  }

  const data = {
    number: payload.doc.docnumber,
    captcha_token: payload.doc.captcha_token,
  };

  try {
    if (userCountry.value === 'MEX') {
      await updateUserDocumentCURP(data);
    } else {
      await updateUserDocumentCPF(data);
    }
  } catch (err: any) {
    openToaster({
      message:
        apiErrorMessage(err?.data?.message) ||
        err?.message ||
        t('deposit.errors.document'),
      type: 'error',
    });

    return false;
  }

  await fetchUserProfile({ force: true });

  return true;
}

// KYC/containment antes do depósito, espelhando o DefaultLayout do fluxo principal.
async function runValidationThenDeposit(
  amountInCents: number,
  saveBankAccount: boolean,
) {
  const { isValidationModuleEnabled, getMinValueToTriggerModule } =
    useModulesValidation();

  const needsValidation =
    isValidationModuleEnabled('deposit') ||
    isValidationModuleEnabled('first_deposit');

  if (!needsValidation) {
    await submitDepositAPI(amountInCents, saveBankAccount);

    return;
  }

  await fetchUserProfile({ force: true });

  let pendingScope: Containment.Scope | null = null;

  if (!hasFirstDeposit.value && isValidationModuleEnabled('first_deposit')) {
    if (amountInCents >= getMinValueToTriggerModule('first_deposit')) {
      await $containment.fetchCurrentPending('first_deposit');
      if ($containment.hasPendencyOn('first_deposit')) {
        pendingScope = 'first_deposit';
      }
    }
  } else if (isValidationModuleEnabled('deposit')) {
    if (amountInCents >= getMinValueToTriggerModule('deposit')) {
      await $containment.fetchCurrentPending('deposit');
      if ($containment.hasPendencyOn('deposit')) {
        pendingScope = 'deposit';
      }
    }
  }

  if (pendingScope) {
    isLoading.value = false;
    await $containment.handle(
      pendingScope,
      async () => {
        isLoading.value = true;
        await submitDepositAPI(amountInCents, saveBankAccount);
        isLoading.value = false;
      },
      { action: 'modal' },
    );

    return;
  }

  await submitDepositAPI(amountInCents, saveBankAccount);
}

// Qual tela a Etapa 2 mostra: cada família de provedor paga de um jeito. PIX
// (e qualquer provedor que devolva código copia-e-cola) usa o StepPix, que é o
// equivalente do DepositResultDefault do fluxo antigo.
const CODE_COPY_METHODS = ['spei', 'oxxo'];
const CHECKOUT_METHODS = [
  'astropay',
  'worldline',
  'credit-card',
  // prontopaga cai na mesma tela de checkout do cartão no fluxo antigo.
  'prontopaga',
];

const paymentScreen = computed<'code' | 'checkout' | 'pix'>(() => {
  if (CODE_COPY_METHODS.includes(currentDepositMethod.value)) return 'code';
  if (CHECKOUT_METHODS.includes(currentDepositMethod.value)) return 'checkout';

  return 'pix';
});

// "Já paguei" das telas que não têm polling próprio (o jogador paga fora do
// app): fecha e atualiza o saldo, igual ao fluxo antigo.
function onPaidOutside() {
  updateWallet();
  close();
}

// "Voltar" das telas de pagamento: volta pro formulário sem fechar o modal.
function onBackToForm() {
  pollGeneration++;
  isCountdownDone.value = true;
  backToForm();
}

function applyPaymentResponse(
  responseData: any,
  displayValue: string | number,
) {
  const fields = [
    'qr_code',
    'br_code',
    'clabe',
    'payment_link',
    'barcode',
    'barcode_number',
    'astropay_response',
    'checkout_page',
  ] as const;

  fields.forEach((key) => {
    if (responseData?.[key]) {
      (paymentRes.value as any)[key] = responseData[key];
    }
  });

  paymentRes.value.value = displayValue;
  paymentRes.value.amount = displayValue;
}

// O `message` do core nem sempre é string: em erro de validação vem o mapa do
// Laravel ({ campo: ["mensagem"] }), que interpolado no toaster virava
// "[object Object]". Devolve a primeira mensagem legível do mapa.
function apiErrorMessage(message: unknown): string {
  if (typeof message === 'string' && message.trim()) return message;

  if (message && typeof message === 'object') {
    for (const value of Object.values(message as Record<string, unknown>)) {
      const first = Array.isArray(value) ? value[0] : value;

      if (typeof first === 'string' && first.trim()) return first;
    }
  }

  return '';
}

async function submitDepositAPI(
  amountInCents: number,
  saveBankAccount: boolean,
) {
  const query_params: Record<string, unknown> = {
    user_id: userId.value,
    credit_amount: amountInCents,
    payment_method: currentDepositMethod.value,
    currency: userCurrency.value,
    // Obrigatório na API: é o consentimento que o jogador deu na Etapa 1
    // (checkbox do StepAmount). Sem o campo a requisição volta 422.
    save_bank_account: saveBankAccount,
    ...(utmCampaign.value ? { utm_campaign: utmCampaign.value } : {}),
    ...(utmSource.value ? { utm_source: utmSource.value } : {}),
    ...(utmMedium.value ? { utm_medium: utmMedium.value } : {}),
  };

  if (gaClientId.value) {
    query_params.ga_client_id = gaClientId.value;
  }

  const displayValue = showMoneyFormat({
    value: amountInCents / 100,
    withCents: true,
  });

  limitAlertError.value = '';

  try {
    const response = await ApiService.post('/wallet/add-credit', query_params);

    if (response.status.toString().startsWith('20')) {
      const responseData = response.data;
      const transactionId = responseData?.transaction_id;

      if (transactionId) makeDepositEvent(transactionId);

      applyPaymentResponse(responseData, displayValue);

      $metrics.push('facebook')?.handle('trackCustom', 'PixGenerated', {
        content_name: 'PIX Gerado',
        currency: useRuntimeConfig().public.brand.currency,
        value: amountInCents / 100,
      });
      $metrics.push('gtm')?.handle({
        event: 'pix_generated',
        ...gtmUserParams(),
        currency: useRuntimeConfig().public.brand.currency,
        value: amountInCents / 100,
        transaction_id: transactionId,
      });
      // Atribuição do app nativo: sem este evento o funil install→depósito do
      // AppsFlyer zera em silêncio.
      $metrics.push('appsflyer')?.handle('af_initiated_checkout', {
        af_price: amountInCents / 100,
        af_content_type: 'deposit_pix',
        af_order_id: transactionId,
      });

      isCountdownDone.value = false;
      goToStep(2);

      if (transactionId) pollTransaction(transactionId, ++pollGeneration);
    } else if (response.status === 429) {
      openChallenge();
    } else {
      openToaster({
        message:
          apiErrorMessage(response.data?.message) ||
          t('deposit.errors.generic'),
        type: 'error',
      });
    }
  } catch (error: any) {
    if (error?.status === 429) {
      openToaster({ message: t('deposit.errors.challenge'), type: 'error' });
      openChallenge();

      return;
    }

    // Erro sem resposta do core (rede/timeout): o fluxo antigo distingue do
    // erro de negócio com uma copy própria.
    if (!error?.data) {
      openToaster({
        message: t('deposit.errors.generic_catch'),
        type: 'error',
        time: 10000,
      });

      return;
    }

    const message =
      apiErrorMessage(error.data.message) || t('deposit.errors.generic');

    if (error.data.limit_error) {
      limitAlertError.value = message;

      return;
    }

    openToaster({ message, type: 'error', time: 10000 });
  }
}

// --- Polling de confirmação --------------------------------------------------

// Geração do polling: invalida polls de sessões anteriores. Se o usuário
// fechar e reabrir/depositar de novo, o poll antigo (gen diferente) não pode
// confirmar a sessão nova nem ficar rodando em paralelo.
let pollGeneration = 0;

async function pollTransaction(transaction_id: string, generation: number) {
  if (isCountdownDone.value || !transaction_id) return;

  await sleep(SEVEN_SECONDS_IN_MS);

  // Para se virou outra sessão, o modal fechou ou saiu da etapa de pagamento.
  if (generation !== pollGeneration || !isOpen.value || step.value !== 2) {
    return;
  }

  // Uma falha de rede numa iteração não pode matar o polling: o jogador pode
  // pagar 30s depois e o modal ficaria em "Aguardando" com o saldo já
  // creditado. Falhou → só tenta de novo na próxima volta.
  let data: any;

  try {
    ({ data } = await ApiService.get(`/wallet/charge/${transaction_id}`));
  } catch {
    await pollTransaction(transaction_id, generation);

    return;
  }

  if (generation !== pollGeneration || !isOpen.value || step.value !== 2) {
    return;
  }

  if (data?.status === 'approved') {
    updateWallet();
    approvedInfo.value = buildApprovedInfo(data);
    goToStep(3);
  } else if (data?.status === 'error') {
    // Guia Depósito §4: `error` = recusa por titularidade divergente (a conta
    // que pagou é de outro CPF) — nenhum valor é debitado. Sem este ramo o
    // polling continuava pra sempre e o jogador ficava em "Aguardando
    // pagamento..." num depósito já recusado.
    isCountdownDone.value = true;
    isDenied.value = true;
  } else if (data?.status === 'expired') {
    isCountdownDone.value = true;

    openToaster({ message: t('deposit.errors.pix_expired'), type: 'error' });
  } else {
    await pollTransaction(transaction_id, generation);
  }
}

// Monta os dados da conta bancária pra tela de aprovado, espelhando o fluxo
// antigo (components/deposit/DefaultLayout.vue).
function buildApprovedInfo(data: any): GameDepositApprovedInfo {
  const bankAccount = data?.bank_account;

  // `hasBankAccount` só diz "o jogador tem alguma conta cadastrada" — não que a
  // conta usada NESTE pagamento é diferente da cadastrada. Sem comparar de
  // fato, o aviso de "conta diferente" aparecia mesmo pagando da própria conta.
  const isRegisteredAccount = bankAccount
    ? userBankAccounts.value.some(
        (account) =>
          account.agency === bankAccount.agency &&
          account.number === bankAccount.number,
      )
    : false;

  return {
    amountLabel: showMoneyFormat({
      value: (data?.amount ?? 0) / 100,
      withCents: true,
    }),
    bankAccountLabel: bankAccount ? bankAccount.bank_name : null,
    agencyAccountLabel: bankAccount
      ? `${t('bank_account.payer_accounts.agency_account', {
          agency: joinDigit(bankAccount.agency, bankAccount.agency_digit),
          number: joinDigit(bankAccount.number, bankAccount.number_digit),
        })} · ${getBankAccountTypeLabel(bankAccount.type, t)}`
      : null,
    differentAccount:
      !!bankAccount && hasBankAccount.value && !isRegisteredAccount,
    isNewAccount: !!bankAccount && !isRegisteredAccount,
  };
}

function onCountdownDone() {
  isCountdownDone.value = true;
}

// --- Ações de UI -------------------------------------------------------------

// Trava o scroll do body + guard anti rubber-band do iOS (compartilhado).
const { scrollerRef } = useSlideUpSheet(isOpen);

// Click-fora só fecha se o gesto COMEÇOU no overlay. Sem isso, ao arrastar os
// chips na horizontal e soltar o mouse fora do sheet, o navegador dispara um
// click cujo target vira o overlay (ancestral comum do mousedown no chip + o
// mouseup fora) e fechava o modal no meio do swipe.
const overlayPressed = ref(false);

function onOverlayPointerDown() {
  // Só dispara com @pointerdown.self → garante que o gesto iniciou no overlay.
  overlayPressed.value = true;
}

function onOverlayClick() {
  if (overlayPressed.value) close();
  overlayPressed.value = false;
}

// Auto-submit PIX (deeplink ?open=pix&value= / window.openPixDeposit):
// se o wallet store pediu auto-submit e o usuário já tem documento, dispara
// o depósito direto ao abrir. Sem documento, apenas pré-preenche o valor
// (initialAmount) e deixa o usuário completar o CPF na Etapa 1.
//
// O consentimento de conta bancária tem o MESMO efeito do CPF que falta: só o
// jogador pode dar, então em método bancário (PIX incluso) o auto-submit não
// acontece — o valor fica pré-preenchido e ele marca a caixa e prossegue.
//
// `immediate`: o modal pode ser aberto antes deste componente montar (rota de
// layout blank → default, ou openPixDeposit antes do chunk async resolver) e
// aí o watch normal nunca veria a transição.
watch(
  isOpen,
  async (open) => {
    if (!open) {
      deeplinkAmount.value = '';

      return;
    }

    if (!autoSubmitDeposit.value || !autoSubmitDepositAmount.value) {
      deeplinkAmount.value = '';

      return;
    }

    // Depósitos suspensos: o deeplink só abre o aviso. Limpa o pedido pra ele
    // não disparar numa abertura futura, depois que a suspensão cair.
    if (isSuspended) {
      deeplinkAmount.value = '';
      clearAutoSubmitDeposit();

      return;
    }

    const amountReais = Number(autoSubmitDepositAmount.value);

    // Guarda ANTES de limpar o store: o StepAmount só monta depois deste watch.
    deeplinkAmount.value =
      !isNaN(amountReais) && amountReais > 0 ? String(amountReais) : '';

    clearAutoSubmitDeposit();

    if (!userHasDocument.value || !deeplinkAmount.value) return;
    if (isBankAccountDepositMethod(currentDepositMethod.value)) return;

    await nextTick();
    await onSubmit({
      amount: Math.round(amountReais * 100),
      doc: { docnumber: '', docnumber_valid: false, captcha_token: '' },
      saveBankAccount: false,
    });
  },
  { immediate: true },
);

// Logout com o modal aberto (ex.: 401 no meio da Etapa 2): o singleton
// não é filho de nada que desmonte, então ficaria sobre a home deslogada
// mostrando o QR e o valor do jogador anterior.
const { isAuthenticated } = storeToRefs(useAuthStore());

watch(isAuthenticated, (authenticated) => {
  if (!authenticated && isOpen.value) close();
});
</script>

<template>
  <Teleport to="body">
    <Transition
      appear
      :duration="300"
      name="gdm"
    >
      <div
        v-if="isOpen"
        class="gdm-overlay"
        :class="{
          'is-desktop': !isMobile,
          'has-pendency': $containment.isVerificationModalOpen,
        }"
        @click.self="onOverlayClick"
        @pointerdown.self="onOverlayPointerDown"
      >
        <div
          ref="scrollerRef"
          class="gdm-sheet"
          :class="{ 'is-desktop': !isMobile }"
          @dragstart.prevent
        >
          <!-- Topbar -->
          <header class="gdm-topbar">
            <span class="gdm-title">{{ title }}</span>
            <button
              aria-label="Fechar"
              class="gdm-close"
              type="button"
              @click="close"
            >
              <svg
                fill="none"
                height="10"
                viewBox="0 0 14 14"
                width="10"
              >
                <path
                  d="M1 1L13 13M13 1L1 13"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="2"
                />
              </svg>
            </button>
          </header>

          <!-- Banner promocional (Etapa 1, desktop e mobile) — acima do stepper.
               Imagem completa servida pelo backoffice (slug por FTD). Sem
               imagem do backoffice, o banner não é exibido. No mobile o sheet
               já rola (max-height 92dvh), então o banner não espreme as etapas. -->
          <div
            v-if="step === 1 && bannerImage && !isSuspended"
            class="gdm-banner-wrap"
          >
            <NuxtLink
              v-if="bannerLink"
              :rel="isExternalBannerLink ? 'noopener noreferrer' : undefined"
              :target="isExternalBannerLink ? '_blank' : undefined"
              :to="bannerLink"
              @click="onBannerClick"
            >
              <img
                alt="Banner de depósito"
                class="gdm-banner-full"
                :src="bannerImage"
              />
            </NuxtLink>
            <img
              v-else
              alt="Banner de depósito"
              class="gdm-banner-full"
              :src="bannerImage"
            />
          </div>

          <!-- Barra de progresso. Na recusa ela sai: o depósito não está na
               Etapa 2, saiu do fluxo. -->
          <CasinoGameDepositModalProgressBar
            v-if="!isDenied && !isSuspended"
            :current="step"
          />

          <div class="gdm-content">
            <BaseLoading
              class="!bg-auth-bg-primary z-20"
              :icon-loader="true"
              :show="isLoading"
            />

            <!-- Recusa (titularidade divergente) atravessa as etapas: é saída
                 do fluxo, não passo dele. -->
            <CasinoGameDepositModalStepSuspended
              v-if="isSuspended"
              @close="close"
            />

            <CasinoGameDepositModalStepDenied
              v-else-if="isDenied"
              @close="close"
              @retry="onBackToForm"
            />

            <CasinoGameDepositModalStepAmount
              v-else-if="step === 1"
              :active-first-deposit-message="activeFirstDepositMessage"
              :first-deposit-message="firstDepositMessage"
              :initial-amount="initialAmount"
              :limit-alert-error="limitAlertError"
              :loading="isLoading"
              :max-deposit-amount="maxDepositAmount"
              :min-deposit-amount="minDepositAmount"
              :user-country="userCountry"
              :user-currency="userCurrency"
              :user-has-document="userHasDocument"
              @submit="onSubmit"
            />

            <template v-else-if="step === 2">
              <CasinoGameDepositModalStepCode
                v-if="paymentScreen === 'code'"
                :deposit="paymentRes"
                :method="currentDepositMethod"
                @back="onBackToForm"
                @paid="onPaidOutside"
              />

              <CasinoGameDepositModalStepCheckout
                v-else-if="paymentScreen === 'checkout'"
                :deposit="paymentRes"
                :method="currentDepositMethod"
                @back="onBackToForm"
                @paid="onPaidOutside"
              />

              <CasinoGameDepositModalStepPix
                v-else
                :deposit="paymentRes"
                @countdown-done="onCountdownDone"
              />
            </template>

            <CasinoGameDepositModalStepSuccess
              v-else
              :info="approvedInfo"
              @close="close"
            />
          </div>

          <div class="gdm-safe-bottom" />
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped lang="scss">
// Transição única (v-if só no overlay): só o .gdm-sheet desliza, via
// `animation` (keyframes). NÃO usar `transition`: o reset global em main.scss
// tem `*:not(.transition){ transition: none !important }`, que mata transition
// no app. O backdrop (overlay) NÃO faz fade de propósito — anima-lo junto com
// o `backdrop-filter: blur` full-screen recompõe o blur a cada frame (caro em
// mobile fraco). O Vue não auto-detecta duração no overlay (ele não tem
// animation), então a prop :duration="300" no <Transition> diz o tempo
// explícito — o overlay fica montado os 300ms enquanto o sheet desliza.

.gdm-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000; // acima do header da game-bar (z-[99999])
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  background: rgba(2, 7, 22, 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);

  // Desktop: card centralizado vertical e horizontalmente.
  &.is-desktop {
    justify-content: center;
    align-items: center;
  }
  //fix para apenas do caso do modal de pendencias (ex: kyc, documento, email) precisar ser aberto para validar o depósito.
  &.has-pendency {
    z-index: 996 !important;
  }
}

.gdm-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  max-height: 92dvh;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: none;
  padding: 16px 16px 24px;
  background: #050c1f;
  // Bloqueia seleção de texto e menu de toque dentro do modal.
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  // Mobile: colado ao rodapé, arredondado só no topo.
  border-radius: 20px 20px 0 0;
  will-change: transform, opacity;

  // Impede arrastar imagens/links (webkit); o @dragstart.prevent cobre o resto.
  :deep(img),
  :deep(a) {
    -webkit-user-drag: none;
  }

  // Desktop: card flutuante centralizado, arredondado nos 4 cantos, com sombra
  // e realce interno (conforme Figma node 19668:22749).
  &.is-desktop {
    width: 512px; // 479 + 33
    max-width: 95vw;
    padding: 24px;
    border-radius: 16px;
    max-height: 88dvh;
    box-shadow:
      0 2px 8px 0 rgba(0, 0, 0, 0.06),
      0 -6px 12px 0 rgba(0, 0, 0, 0.03),
      0 14px 28px 0 rgba(0, 0, 0, 0.08),
      inset 0 0 1px 0 rgba(255, 255, 255, 0.3);
  }
}

// Slide do sheet via keyframe global slide-up-spring (translateY + opacity),
// atrelado ao enter/leave do overlay. Declarado depois de .gdm-sheet por causa
// do no-descending-specificity do stylelint. Animation (não transition) pra
// escapar do reset global `transition: none !important`.
.gdm-enter-active .gdm-sheet {
  animation: slide-up-spring 0.3s cubic-bezier(0.22, 0.61, 0.36, 1);
}

// `forwards` é essencial: segura o último frame (fora da tela) até o Vue
// remover. Sem ele, ao fim da animação o sheet volta ao estado base (aberto)
// por 1-2 frames antes da remoção — daí o flash do modal aberto.
.gdm-leave-active .gdm-sheet {
  animation: slide-up-spring 0.3s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
}

.gdm-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;

  .gdm-title {
    color: #fcfcfc;
    font-weight: 700;
    font-size: 15px;
    line-height: 20px;
  }

  .gdm-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    color: #fff;
    cursor: pointer;
    background: rgba(255, 255, 255, 0.05);
    transition: background 0.15s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.16);
    }
  }
}

.gdm-banner-wrap {
  position: relative;
}

// Banner completo servido pelo backoffice (/banner-deposito), com fallback pro
// asset estático. Ocupa a largura toda mantendo a altura do card original.
.gdm-banner-full {
  display: block;
  width: 100%;
  border-radius: 12px;
  object-fit: contain;
}

.gdm-content {
  position: relative;
  display: flex;
  flex-direction: column;
}

.gdm-safe-bottom {
  height: env(safe-area-inset-bottom, 0px);
}
</style>
